/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * 계보(Lineage) 관련 Mock Store
 * 실제 Iceberg 데이터(Bronze/Silver/Gold)를 기반으로 데이터 흐름을 시각화합니다.
 */
import type { PlatformLineageResponse, PlatformLineageView } from '@/features/lineage/api';

/**
 * 모든 테이블 목록 정의
 */
const ALL_TABLES = {
  bronze: [
    'gsc_bearings',
    'gsp_dcs',
    'gsp_ecms1_bearings1',
    'gsp_ecms1_bearings2',
    'gsp_ecms1_chart',
    'gsp_ecms1_trend',
    'hanwha_impact_bearings1',
    'hanwha_impact_chart',
    'hanwha_impact_trend',
    'kmp_ecms_bearings1',
    'kmp_ecms_chart',
    'kmp_ecms_trend',
  ],
  silver: [
    'gsp_ecms1_config',
    'gsp_ecms1_waveform_preprocessed',
    'hanwha_impact_config',
    'hanwha_impact_waveform_preprocessed',
    'kmp_ecms_config',
    'kmp_ecms_waveform_preprocessed',
  ],
  gold: [
    'gsp_ecms1_diagnosis_result',
    'gsp_ecms1_feature_preprocessed',
    'hanwha_impact_diagnosis_results',
    'hanwha_impact_feature_preprocessed',
    'kmp_ecms_diagnosis_results',
    'kmp_ecms_feature_preprocessed',
  ],
};

/**
 * Bronze → Silver → Gold 파이프라인 매핑
 */
const PIPELINE_MAPPINGS = {
  // GSP ECMS1 계열
  gsp_ecms1: {
    bronze: ['gsp_ecms1_chart', 'gsp_ecms1_trend', 'gsp_ecms1_bearings1', 'gsp_ecms1_bearings2'],
    silver: ['gsp_ecms1_waveform_preprocessed', 'gsp_ecms1_config'],
    gold: ['gsp_ecms1_feature_preprocessed', 'gsp_ecms1_diagnosis_result'],
  },
  // KMP ECMS 계열
  kmp_ecms: {
    bronze: ['kmp_ecms_bearings1', 'kmp_ecms_chart', 'kmp_ecms_trend'],
    silver: ['kmp_ecms_waveform_preprocessed', 'kmp_ecms_config'],
    gold: ['kmp_ecms_feature_preprocessed', 'kmp_ecms_diagnosis_results'],
  },
  // Hanwha Impact 계열
  hanwha_impact: {
    bronze: ['hanwha_impact_bearings1', 'hanwha_impact_chart', 'hanwha_impact_trend'],
    silver: ['hanwha_impact_waveform_preprocessed', 'hanwha_impact_config'],
    gold: ['hanwha_impact_feature_preprocessed', 'hanwha_impact_diagnosis_results'],
  },
};

/**
 * Service View: 데이터 서비스 간의 연결 관계
 * Bronze → Silver → Gold 레이어 흐름 (모든 테이블 자동 생성)
 */
const getServiceViewLineage = (): PlatformLineageResponse => {
  const nodes: any = {};
  const edges: any = {};
  let edgeCounter = 1;

  // 1. 기본 구조 노드 및 엣지 생성 (Service, Database, Schema)
  const layers = ['bronze', 'silver', 'gold'] as const;

  layers.forEach((layer) => {
    // Service 노드
    nodes[`${layer}-iceberg`] = {
      entity: {
        id: `${layer}-iceberg-service-id`,
        name: `${layer}-iceberg`,
        displayName: `${layer.charAt(0).toUpperCase() + layer.slice(1)} Iceberg Service`,
        fullyQualifiedName: `${layer}-iceberg`,
        entityType: 'databaseService',
        serviceType: 'Iceberg',
        description: `${layer.charAt(0).toUpperCase() + layer.slice(1)} Tier 데이터 저장소`,
        deleted: false,
        fqnHash: `${layer}-iceberg-hash`,
        updatedAt: Date.now(),
      },
    };

    // Database 노드
    nodes[`${layer}-iceberg.${layer}`] = {
      entity: {
        id: `${layer}-db-id`,
        name: layer,
        displayName: `${layer.charAt(0).toUpperCase() + layer.slice(1)} Database`,
        fullyQualifiedName: `${layer}-iceberg.${layer}`,
        entityType: 'database',
        description: `${layer.charAt(0).toUpperCase() + layer.slice(1)} 레이어 데이터베이스`,
        deleted: false,
        fqnHash: `${layer}-db-hash`,
        updatedAt: Date.now(),
      },
    };

    // Schema 노드
    nodes[`${layer}-iceberg.${layer}.g_turbo`] = {
      entity: {
        id: `${layer}-g_turbo-schema-id`,
        name: 'g_turbo',
        displayName: `g_turbo Schema (${layer.charAt(0).toUpperCase() + layer.slice(1)})`,
        fullyQualifiedName: `${layer}-iceberg.${layer}.g_turbo`,
        entityType: 'databaseSchema',
        description: `터보 관련 ${layer} 데이터 스키마`,
        deleted: false,
        fqnHash: `${layer}-g_turbo-hash`,
        updatedAt: Date.now(),
      },
    };

    // Service → Database 엣지
    edges[`edge-${edgeCounter++}`] = {
      fromEntity: {
        id: `${layer}-iceberg-service-id`,
        type: 'databaseService',
        name: `${layer}-iceberg`,
        fullyQualifiedName: `${layer}-iceberg`,
      },
      toEntity: {
        id: `${layer}-db-id`,
        type: 'database',
        name: layer,
        fullyQualifiedName: `${layer}-iceberg.${layer}`,
      },
      source: 'IcebergService',
      docId: `doc-${edgeCounter}`,
      docUniqueId: `unique-${edgeCounter}`,
      createdAt: Date.now(),
      createdBy: 'admin',
      updatedAt: Date.now(),
      updatedBy: 'admin',
      assetEdges: 1,
    };

    // Database → Schema 엣지
    edges[`edge-${edgeCounter++}`] = {
      fromEntity: {
        id: `${layer}-db-id`,
        type: 'database',
        name: layer,
        fullyQualifiedName: `${layer}-iceberg.${layer}`,
      },
      toEntity: {
        id: `${layer}-g_turbo-schema-id`,
        type: 'databaseSchema',
        name: 'g_turbo',
        fullyQualifiedName: `${layer}-iceberg.${layer}.g_turbo`,
      },
      source: 'IcebergService',
      docId: `doc-${edgeCounter}`,
      docUniqueId: `unique-${edgeCounter}`,
      createdAt: Date.now(),
      createdBy: 'admin',
      updatedAt: Date.now(),
      updatedBy: 'admin',
      assetEdges: ALL_TABLES[layer].length,
    };
  });

  // 2. 모든 테이블 노드 생성
  layers.forEach((layer) => {
    ALL_TABLES[layer].forEach((tableName) => {
      const fqn = `${layer}-iceberg.${layer}.g_turbo.${tableName}`;
      nodes[fqn] = {
        entity: {
          id: `${layer}-${tableName}-id`,
          name: tableName,
          displayName: `${tableName} (${layer.charAt(0).toUpperCase() + layer.slice(1)})`,
          fullyQualifiedName: fqn,
          entityType: 'table',
          description: `${tableName} 테이블`,
          deleted: false,
          fqnHash: `${layer}-${tableName}-hash`,
          updatedAt: Date.now(),
        },
      };

      // Schema → Table 엣지
      edges[`edge-${edgeCounter++}`] = {
        fromEntity: {
          id: `${layer}-g_turbo-schema-id`,
          type: 'databaseSchema',
          name: 'g_turbo',
          fullyQualifiedName: `${layer}-iceberg.${layer}.g_turbo`,
        },
        toEntity: {
          id: `${layer}-${tableName}-id`,
          type: 'table',
          name: tableName,
          fullyQualifiedName: fqn,
        },
        source: 'IcebergService',
        docId: `doc-${edgeCounter}`,
        docUniqueId: `unique-${edgeCounter}`,
        createdAt: Date.now(),
        createdBy: 'admin',
        updatedAt: Date.now(),
        updatedBy: 'admin',
        assetEdges: 1,
      };
    });
  });

  // 3. 데이터 파이프라인 엣지 생성 (Bronze → Silver → Gold)
  // 실제 upstreamLineage 데이터를 반영한 정확한 연결 관계

  // Bronze → Silver 파이프라인
  Object.entries(PIPELINE_MAPPINGS).forEach(([_family, mapping]) => {
    // waveform 테이블: 해당 계열의 모든 Bronze 테이블로부터 데이터 수신
    const silverWaveform = mapping.silver.find((t) => t.includes('waveform'));
    if (silverWaveform) {
      mapping.bronze.forEach((bronzeTable) => {
        edges[`edge-${edgeCounter++}`] = {
          fromEntity: {
            id: `bronze-${bronzeTable}-id`,
            type: 'table',
            name: bronzeTable,
            fullyQualifiedName: `bronze-iceberg.bronze.g_turbo.${bronzeTable}`,
          },
          toEntity: {
            id: `silver-${silverWaveform}-id`,
            type: 'table',
            name: silverWaveform,
            fullyQualifiedName: `silver-iceberg.silver.g_turbo.${silverWaveform}`,
          },
          source: 'PipelineService',
          docId: `doc-${edgeCounter}`,
          docUniqueId: `unique-${edgeCounter}`,
          createdAt: Date.now(),
          createdBy: 'admin',
          updatedAt: Date.now(),
          updatedBy: 'admin',
          assetEdges: 1,
        };
      });
    }

    // config 테이블: bearings1 테이블로부터만 데이터 수신
    const silverConfig = mapping.silver.find((t) => t.includes('config'));
    const bronzeBearings = mapping.bronze.find((t) => t.includes('bearings1'));
    if (silverConfig && bronzeBearings) {
      edges[`edge-${edgeCounter++}`] = {
        fromEntity: {
          id: `bronze-${bronzeBearings}-id`,
          type: 'table',
          name: bronzeBearings,
          fullyQualifiedName: `bronze-iceberg.bronze.g_turbo.${bronzeBearings}`,
        },
        toEntity: {
          id: `silver-${silverConfig}-id`,
          type: 'table',
          name: silverConfig,
          fullyQualifiedName: `silver-iceberg.silver.g_turbo.${silverConfig}`,
        },
        source: 'PipelineService',
        docId: `doc-${edgeCounter}`,
        docUniqueId: `unique-${edgeCounter}`,
        createdAt: Date.now(),
        createdBy: 'admin',
        updatedAt: Date.now(),
        updatedBy: 'admin',
        assetEdges: 1,
      };
    }
  });

  // Silver → Gold 파이프라인
  Object.entries(PIPELINE_MAPPINGS).forEach(([_family, mapping]) => {
    const goldFeature = mapping.gold.find((t) => t.includes('feature'));

    if (goldFeature) {
      // 각 Silver 테이블이 Gold Feature 테이블로 연결
      mapping.silver.forEach((silverTable) => {
        edges[`edge-${edgeCounter++}`] = {
          fromEntity: {
            id: `silver-${silverTable}-id`,
            type: 'table',
            name: silverTable,
            fullyQualifiedName: `silver-iceberg.silver.g_turbo.${silverTable}`,
          },
          toEntity: {
            id: `gold-${goldFeature}-id`,
            type: 'table',
            name: goldFeature,
            fullyQualifiedName: `gold-iceberg.gold.g_turbo.${goldFeature}`,
          },
          source: 'PipelineService',
          docId: `doc-${edgeCounter}`,
          docUniqueId: `unique-${edgeCounter}`,
          createdAt: Date.now(),
          createdBy: 'admin',
          updatedAt: Date.now(),
          updatedBy: 'admin',
          assetEdges: 1,
        };
      });
    }

    // Gold Feature → Gold Diagnosis
    const goldDiagnosis = mapping.gold.find((t) => t.includes('diagnosis'));
    if (goldFeature && goldDiagnosis) {
      edges[`edge-${edgeCounter++}`] = {
        fromEntity: {
          id: `gold-${goldFeature}-id`,
          type: 'table',
          name: goldFeature,
          fullyQualifiedName: `gold-iceberg.gold.g_turbo.${goldFeature}`,
        },
        toEntity: {
          id: `gold-${goldDiagnosis}-id`,
          type: 'table',
          name: goldDiagnosis,
          fullyQualifiedName: `gold-iceberg.gold.g_turbo.${goldDiagnosis}`,
        },
        source: 'PipelineService',
        docId: `doc-${edgeCounter}`,
        docUniqueId: `unique-${edgeCounter}`,
        createdAt: Date.now(),
        createdBy: 'admin',
        updatedAt: Date.now(),
        updatedBy: 'admin',
        assetEdges: 1,
      };
    }
  });

  // 4. 단독 Bronze 테이블 처리
  // gsc_bearings와 gsp_dcs는 특정 Silver와 연결되지 않은 독립 Bronze 테이블
  const standaloneBronzeTables = ['gsc_bearings', 'gsp_dcs'];
  standaloneBronzeTables.forEach((tableName) => {
    // GSP 계열 Silver waveform으로 일반 연결
    edges[`edge-${edgeCounter++}`] = {
      fromEntity: {
        id: `bronze-${tableName}-id`,
        type: 'table',
        name: tableName,
        fullyQualifiedName: `bronze-iceberg.bronze.g_turbo.${tableName}`,
      },
      toEntity: {
        id: 'silver-gsp_ecms1_waveform_preprocessed-id',
        type: 'table',
        name: 'gsp_ecms1_waveform_preprocessed',
        fullyQualifiedName: 'silver-iceberg.silver.g_turbo.gsp_ecms1_waveform_preprocessed',
      },
      source: 'PipelineService',
      docId: `doc-${edgeCounter}`,
      docUniqueId: `unique-${edgeCounter}`,
      createdAt: Date.now(),
      createdBy: 'admin',
      updatedAt: Date.now(),
      updatedBy: 'admin',
      assetEdges: 1,
    };
  });

  return {
    nodes,
    upstreamEdges: edges,
    downstreamEdges: {},
  } as unknown as PlatformLineageResponse;
};

/**
 * Domain View: 도메인 간의 데이터 흐름
 * Manufacturing -> Quality -> Operations
 */
const getDomainViewLineage = (): PlatformLineageResponse => {
  return {
    nodes: {
      Manufacturing: {
        entity: {
          id: 'manufacturing-domain-id',
          name: 'Manufacturing',
          displayName: '제조',
          fullyQualifiedName: 'Manufacturing',
          entityType: 'domain',
          domainType: 'Aggregate',
          description: '제조 관련 모든 데이터와 프로세스를 관리하는 도메인',
          deleted: false,
          fqnHash: 'manufacturing-hash',
          updatedAt: Date.now(),
        },
      },
      'Manufacturing.Site_A.CNC_Cell.Turning': {
        entity: {
          id: 'turning-domain-id',
          name: 'Turning',
          displayName: '터닝 공정',
          fullyQualifiedName: 'Manufacturing.Site_A.CNC_Cell.Turning',
          entityType: 'domain',
          domainType: 'Source-aligned',
          description: 'CNC 터닝 설비 및 공정 데이터',
          deleted: false,
          fqnHash: 'turning-hash',
          updatedAt: Date.now(),
          parent: {
            id: 'cnc-cell-id',
            name: 'CNC_Cell',
            displayName: 'CNC 셀',
            fullyQualifiedName: 'Manufacturing.Site_A.CNC_Cell',
            type: 'domain',
          },
        },
      },
      'Manufacturing.Site_B.MCT_Cell.Milling': {
        entity: {
          id: 'milling-domain-id',
          name: 'Milling',
          displayName: '밀링 공정',
          fullyQualifiedName: 'Manufacturing.Site_B.MCT_Cell.Milling',
          entityType: 'domain',
          domainType: 'Source-aligned',
          description: 'MCT 밀링 설비 및 공정 데이터',
          deleted: false,
          fqnHash: 'milling-hash',
          updatedAt: Date.now(),
          parent: {
            id: 'mct-cell-id',
            name: 'MCT_Cell',
            displayName: 'MCT 셀',
            fullyQualifiedName: 'Manufacturing.Site_B.MCT_Cell',
            type: 'domain',
          },
        },
      },
      Quality: {
        entity: {
          id: 'quality-domain-id',
          name: 'Quality',
          displayName: '품질 관리',
          fullyQualifiedName: 'Quality',
          entityType: 'domain',
          domainType: 'Aggregate',
          description: '품질 검사 및 관리 데이터',
          deleted: false,
          fqnHash: 'quality-hash',
          updatedAt: Date.now(),
        },
      },
      'Quality.Inspection.Dimensional': {
        entity: {
          id: 'dimensional-domain-id',
          name: 'Dimensional',
          displayName: '치수 검사',
          fullyQualifiedName: 'Quality.Inspection.Dimensional',
          entityType: 'domain',
          domainType: 'Consumer-aligned',
          description: '제품 치수 검사 데이터',
          deleted: false,
          fqnHash: 'dimensional-hash',
          updatedAt: Date.now(),
          parent: {
            id: 'inspection-id',
            name: 'Inspection',
            displayName: '검사',
            fullyQualifiedName: 'Quality.Inspection',
            type: 'domain',
          },
        },
      },
      Operations: {
        entity: {
          id: 'operations-domain-id',
          name: 'Operations',
          displayName: '운영',
          fullyQualifiedName: 'Operations',
          entityType: 'domain',
          domainType: 'Aggregate',
          description: '생산 운영 및 모니터링 데이터',
          deleted: false,
          fqnHash: 'operations-hash',
          updatedAt: Date.now(),
        },
      },
    },
    upstreamEdges: {
      'domain-edge-1': {
        fromEntity: {
          id: 'turning-domain-id',
          type: 'domain',
          name: 'Turning',
          fullyQualifiedName: 'Manufacturing.Site_A.CNC_Cell.Turning',
        },
        toEntity: {
          id: 'dimensional-domain-id',
          type: 'domain',
          name: 'Dimensional',
          fullyQualifiedName: 'Quality.Inspection.Dimensional',
        },
        source: 'Manual',
        docId: 'domain-doc-1',
        docUniqueId: 'domain-unique-1',
        createdAt: Date.now(),
        createdBy: 'admin',
        updatedAt: Date.now(),
        updatedBy: 'admin',
        assetEdges: 15,
      },
      'domain-edge-2': {
        fromEntity: {
          id: 'milling-domain-id',
          type: 'domain',
          name: 'Milling',
          fullyQualifiedName: 'Manufacturing.Site_B.MCT_Cell.Milling',
        },
        toEntity: {
          id: 'dimensional-domain-id',
          type: 'domain',
          name: 'Dimensional',
          fullyQualifiedName: 'Quality.Inspection.Dimensional',
        },
        source: 'Manual',
        docId: 'domain-doc-2',
        docUniqueId: 'domain-unique-2',
        createdAt: Date.now(),
        createdBy: 'admin',
        updatedAt: Date.now(),
        updatedBy: 'admin',
        assetEdges: 12,
      },
      'domain-edge-3': {
        fromEntity: {
          id: 'dimensional-domain-id',
          type: 'domain',
          name: 'Dimensional',
          fullyQualifiedName: 'Quality.Inspection.Dimensional',
        },
        toEntity: {
          id: 'operations-domain-id',
          type: 'domain',
          name: 'Operations',
          fullyQualifiedName: 'Operations',
        },
        source: 'Manual',
        docId: 'domain-doc-3',
        docUniqueId: 'domain-unique-3',
        createdAt: Date.now(),
        createdBy: 'admin',
        updatedAt: Date.now(),
        updatedBy: 'admin',
        assetEdges: 8,
      },
    },
    downstreamEdges: {},
  } as unknown as PlatformLineageResponse;
};

/**
 * Data Product View: 데이터 제품 간의 관계
 * 실험 데이터 제품 중심
 */
const getDataProductViewLineage = (): PlatformLineageResponse => {
  return {
    nodes: {
      'manufacturing-insights': {
        entity: {
          id: 'manufacturing-insights-id',
          name: 'manufacturing-insights',
          displayName: 'Manufacturing Insights',
          fullyQualifiedName: 'manufacturing-insights',
          entityType: 'dataProduct',
          description: '제조 공정 인사이트 데이터 제품',
          deleted: false,
          fqnHash: 'manufacturing-insights-hash',
          updatedAt: Date.now(),
          domains: [
            {
              id: 'manufacturing-domain-id',
              name: 'Manufacturing',
              displayName: '제조',
              fullyQualifiedName: 'Manufacturing',
            },
          ],
        },
      },
      'quality-analytics': {
        entity: {
          id: 'quality-analytics-id',
          name: 'quality-analytics',
          displayName: 'Quality Analytics',
          fullyQualifiedName: 'quality-analytics',
          entityType: 'dataProduct',
          description: '품질 분석 데이터 제품',
          deleted: false,
          fqnHash: 'quality-analytics-hash',
          updatedAt: Date.now(),
          domains: [
            {
              id: 'quality-domain-id',
              name: 'Quality',
              displayName: '품질 관리',
              fullyQualifiedName: 'Quality',
            },
          ],
        },
      },
      'predictive-maintenance': {
        entity: {
          id: 'predictive-maintenance-id',
          name: 'predictive-maintenance',
          displayName: 'Predictive Maintenance',
          fullyQualifiedName: 'predictive-maintenance',
          entityType: 'dataProduct',
          description: '예측 정비 데이터 제품',
          deleted: false,
          fqnHash: 'predictive-maintenance-hash',
          updatedAt: Date.now(),
          domains: [
            {
              id: 'operations-domain-id',
              name: 'Operations',
              displayName: '운영',
              fullyQualifiedName: 'Operations',
            },
          ],
        },
      },
    },
    upstreamEdges: {
      'dp-edge-1': {
        fromEntity: {
          id: 'manufacturing-insights-id',
          type: 'dataProduct',
          name: 'manufacturing-insights',
          fullyQualifiedName: 'manufacturing-insights',
        },
        toEntity: {
          id: 'quality-analytics-id',
          type: 'dataProduct',
          name: 'quality-analytics',
          fullyQualifiedName: 'quality-analytics',
        },
        source: 'Manual',
        docId: 'dp-doc-1',
        docUniqueId: 'dp-unique-1',
        createdAt: Date.now(),
        createdBy: 'admin',
        updatedAt: Date.now(),
        updatedBy: 'admin',
        assetEdges: 24,
      },
      'dp-edge-2': {
        fromEntity: {
          id: 'quality-analytics-id',
          type: 'dataProduct',
          name: 'quality-analytics',
          fullyQualifiedName: 'quality-analytics',
        },
        toEntity: {
          id: 'predictive-maintenance-id',
          type: 'dataProduct',
          name: 'predictive-maintenance',
          fullyQualifiedName: 'predictive-maintenance',
        },
        source: 'Manual',
        docId: 'dp-doc-2',
        docUniqueId: 'dp-unique-2',
        createdAt: Date.now(),
        createdBy: 'admin',
        updatedAt: Date.now(),
        updatedBy: 'admin',
        assetEdges: 18,
      },
      'dp-edge-3': {
        fromEntity: {
          id: 'manufacturing-insights-id',
          type: 'dataProduct',
          name: 'manufacturing-insights',
          fullyQualifiedName: 'manufacturing-insights',
        },
        toEntity: {
          id: 'predictive-maintenance-id',
          type: 'dataProduct',
          name: 'predictive-maintenance',
          fullyQualifiedName: 'predictive-maintenance',
        },
        source: 'Manual',
        docId: 'dp-doc-3',
        docUniqueId: 'dp-unique-3',
        createdAt: Date.now(),
        createdBy: 'admin',
        updatedAt: Date.now(),
        updatedBy: 'admin',
        assetEdges: 12,
      },
    },
    downstreamEdges: {},
  } as unknown as PlatformLineageResponse;
};

/**
 * View에 따른 계보 데이터 반환
 */
export const getPlatformLineageMockData = (view: PlatformLineageView): PlatformLineageResponse => {
  switch (view) {
    case 'service':
      return getServiceViewLineage();
    case 'domain':
      return getDomainViewLineage();
    case 'dataProduct':
      return getDataProductViewLineage();
    default:
      return getServiceViewLineage();
  }
};

/**
 * 개별 자산의 계보 데이터 생성
 * 최상위 시작점(데이터베이스 서비스)에서 자기 자신까지의 순차적 계보만 표시
 */
export const getAssetLineageMockData = (fqn: string): PlatformLineageResponse => {
  const platformLineage = getServiceViewLineage();
  const allNodes = platformLineage.nodes;
  const allEdges = platformLineage.upstreamEdges;

  // 해당 자산이 존재하지 않으면 빈 계보 반환
  if (!allNodes[fqn]) {
    return {
      nodes: {},
      upstreamEdges: {},
      downstreamEdges: {},
    };
  }

  // 관련 노드와 엣지를 추출 (자기 자신까지만)
  const relevantNodes: typeof allNodes = {};
  const relevantUpstreamEdges: typeof allEdges = {};

  // 현재 자산부터 시작해서 최상위까지 upstream 추적
  const collectUpstreamToRoot = (currentFqn: string, depth: number = 0) => {
    if (depth > 10) return;

    relevantNodes[currentFqn] = allNodes[currentFqn];

    // 현재 노드로 들어오는 엣지 찾기 (upstream)
    Object.entries(allEdges).forEach(([edgeId, edge]) => {
      if (edge.toEntity.fullyQualifiedName === currentFqn) {
        relevantUpstreamEdges[edgeId] = edge;
        const sourceFqn = edge.fromEntity.fullyQualifiedName;
        if (allNodes[sourceFqn] && !relevantNodes[sourceFqn]) {
          collectUpstreamToRoot(sourceFqn, depth + 1);
        }
      }
    });
  };

  // 자기 자신부터 최상위까지 upstream만 수집 (downstream은 수집하지 않음)
  collectUpstreamToRoot(fqn);

  return {
    nodes: relevantNodes,
    upstreamEdges: relevantUpstreamEdges,
    downstreamEdges: {}, // 자기 자신이 마지막이므로 downstream은 없음
  };
};
